import { useState } from 'react';
import { ROUTE_DASHBOARD } from '@/constants/routes';
import { RouterLink } from '@redux-first-router-link';
import If from '@/components/If';
import { useTracking } from 'react-tracking';
import { NavAnchor, NavBarItem, RegisterButton, Icon } from './styled';
import loadable from '@loadable/component';
import useUser from '@/hooks/useUser';
import { useUtmExperiment } from '@/hooks/usePlanUtmExperiment';

const GuestRegistrationModal = loadable(
  () => import(/* webpackPrefetch: true */ '@components/GuestRegistrationModal')
);

interface Props {
  isWhite?: boolean;
}

export const CTA = ({ isWhite }: Props) => {
  const [showRegistration, setShowRegistration] = useState(false);
  const { trackEvent } = useTracking();
  const user = useUser();

  const { isUtmExperiment } = useUtmExperiment();

  return (
    <>
      <If
        conditions={{
          loggedOut: user.isFetched && !user.data.loggedIn,
          loggedIn: user.isFetched && user.data.loggedIn,
        }}
      >
        {{
          loggedOut: (
            <>
              {!isUtmExperiment && (
                <NavBarItem>
                  <RegisterButton
                    styleType="gentleLargeReverse"
                    onClick={() => {
                      setShowRegistration(true);
                      trackEvent({
                        type: 'track',
                        action: 'click',
                        modifier: 'cta',
                        subject: 'guestRegistrationModal',
                      });
                    }}
                    data-cy-id="nav-register"
                  >
                    Register for a Free Plan
                    <Icon />
                  </RegisterButton>
                </NavBarItem>
              )}
              <NavBarItem>
                <NavAnchor
                  data-cy-id="navigation-login"
                  onClick={() => {
                    trackEvent({
                      action: 'click',
                      modifier: 'login',
                    });
                    if (isUtmExperiment) {
                      setShowRegistration(true);
                    }
                  }}
                  href={!isUtmExperiment ? '/welcome' : undefined}
                  $isWhite={isWhite}
                >
                  Login
                </NavAnchor>
              </NavBarItem>
            </>
          ),
          loggedIn: (
            <NavBarItem>
              <RouterLink
                data-cy-id="navigation-app"
                inheritColor
                noUnderline
                to={{ type: ROUTE_DASHBOARD }}
              >
                <RegisterButton as="div" styleType="gentleLargeReverse">
                  Go to platform
                </RegisterButton>
              </RouterLink>
            </NavBarItem>
          ),
        }}
      </If>
      <If match={showRegistration}>
        {() => (
          <GuestRegistrationModal
            initialType={isUtmExperiment ? 'login' : undefined}
            canClose
            onClose={() => setShowRegistration(false)}
            onRegisterSuccess={() => {
              trackEvent({
                type: 'track',
                subject: 'cta',
                modifier: 'registration',
                action: 'success',
              });
            }}
            onLoginSuccess={() => {
              if (isUtmExperiment) {
                setShowRegistration(false);
              }
            }}
          />
        )}
      </If>
    </>
  );
};
